/**
 * cache/mission.ts — the mission graph and the builder the cycle library writes to.
 *
 * Dormans' central claim (Adventures in Level Design, PCG Workshop 2010) is that a
 * dungeon's MISSION — the directed graph of tasks — and its SPACE — the network of
 * rooms — are separate structures that must be generated in separate steps. This file
 * is the mission half. It knows nothing about metres, cells or geometry.
 *
 * THE SHAPE WE BUILD. Unexplored composes dungeons from **cycles**: a loop with an
 * entrance and a goal on it, split by those two into two arcs. We adopt the loop but
 * NOT "goal on the loop", because that reading makes lock-and-key unsound — if both
 * arcs terminate at the goal, a gate on one arc is simply bypassed by the other, and
 * the key means nothing. Instead:
 *
 *      E ──arc A──▶ J ──arc B──▶ E          (a real, fully traversable loop)
 *                   │
 *                   └─[gate]─▶ … ─▶ B       (the goal hangs off the loop)
 *
 * The loop is always reachable UNGATED from the entrance, so every key on it is
 * always obtainable, so every gate on the goal stub always opens. **Solvability is
 * structural.** {@link missionSolvable} is therefore an assertion we expect to hold,
 * not a filter we expect to reject things — see solve.ts.
 *
 * LOCK/KEY PAIRS. An edge's `lock` names the room index of the key that opens it —
 * the Unexplored "special edge" that keeps the pair consistent however the graph is
 * shuffled afterwards.
 */
import type { Rng } from "../rng.js";
import { pick, rangeInt } from "../rng.js";
import type { CacheEdgeKind, CacheRole } from "./types.js";

export interface MissionNode {
  i: number;
  role: CacheRole;
  /** True for nodes on the main loop; false for goal-stub and spoke nodes. */
  onLoop: boolean;
  /** For off-loop nodes: the node they hang from. -1 on loop nodes. */
  anchor: number;
  /** Relative encounter pressure, 0..3 — the space layer turns this into a budget. */
  pressure: number;
}

export interface MissionEdge {
  a: number;
  b: number;
  kind: CacheEdgeKind;
  /** Room index of the key opening this edge, or -1. */
  lock: number;
  /** True when the edge may only be traversed a→b (the `drop` shortcut). */
  oneWay: boolean;
}

export interface MissionGraph {
  cycle: string;
  tier: number;
  nodes: MissionNode[];
  edges: MissionEdge[];
  entrance: number;
  boss: number;
  /** Loop node indices in ring order, starting at the entrance. */
  loop: number[];
  /** Off-loop node indices, in the order they must be placed (anchors first). */
  offLoop: number[];
}

/**
 * The API the cycle library builds against. Deliberately tiny: add nodes, link them,
 * declare the loop. Everything a cycle wants to express — arcs, gates, keys, spokes,
 * one-way shortcuts — is those three operations.
 */
export class MissionBuilder {
  readonly nodes: MissionNode[] = [];
  readonly edges: MissionEdge[] = [];
  /** Loop ring order, filled by {@link closeLoop}. */
  private ring: number[] = [];

  constructor(readonly rng: Rng, readonly tier: number) {}

  /** Add a node. Loop membership is decided by {@link closeLoop}; `anchor` links off-loop nodes. */
  node(role: CacheRole, opts: { anchor?: number; pressure?: number } = {}): number {
    const i = this.nodes.length;
    this.nodes.push({
      i, role, onLoop: false,
      anchor: opts.anchor ?? -1,
      pressure: opts.pressure ?? (role === "arena" || role === "boss" ? 2 : role === "transit" ? 1 : 0),
    });
    return i;
  }

  /** Link two nodes. `lock` is the room index of the key that opens the edge. */
  link(a: number, b: number, kind: CacheEdgeKind = "door", lock = -1): void {
    this.edges.push({ a, b, kind, lock, oneWay: kind === "drop" });
  }

  /**
   * Declare the ring, in order, starting at the entrance and returning to it. Marks
   * every listed node as on-loop and chains them (the closing edge included), so a
   * cycle body never writes the loop's own edges by hand. `closingKind` overrides the
   * kind of the wrap-around edge only — that is how `shortcut-loop` makes its return
   * leg a one-way `drop` without hand-writing the whole ring.
   */
  closeLoop(ring: readonly number[], kind: CacheEdgeKind = "door", closingKind?: CacheEdgeKind): void {
    this.ring = [...ring];
    for (const i of ring) this.nodes[i].onLoop = true;
    for (let k = 0; k < ring.length; k++) {
      const last = k === ring.length - 1;
      this.link(ring[k], ring[(k + 1) % ring.length], last ? (closingKind ?? kind) : kind);
    }
  }

  /** A chain of `n` transit nodes, returned in order. The arc-filling primitive. */
  transitRun(n: number): number[] {
    const out: number[] = [];
    for (let k = 0; k < n; k++) out.push(this.node("transit"));
    return out;
  }

  finish(cycle: string, entrance: number, boss: number): MissionGraph {
    // Every node must be ON the ring or ANCHORED to something. A node that is neither is
    // an orphan: it will be seated somewhere on the grid with no edges touching it, and
    // the player will never reach it. That is a cycle-definition bug, so it throws here
    // rather than surviving to become a mysteriously walled-off room. (It has happened:
    // an arc builder that overwrote a slot left the displaced node alive and edgeless.)
    for (const n of this.nodes) {
      if (n.onLoop || n.anchor >= 0) continue;
      throw new Error(`cache mission "${cycle}": node ${n.i} (${n.role}) is neither on the loop nor anchored`);
    }

    // Off-loop nodes must be placed anchor-first, so the space layer always has a
    // placed cell to hang the next one off. Repeatedly emit any node whose anchor is
    // already emitted (or is a loop node) — a topological order over the anchor tree.
    const placed = new Set<number>(this.ring);
    const offLoop: number[] = [];
    let progress = true;
    while (progress) {
      progress = false;
      for (const n of this.nodes) {
        if (placed.has(n.i)) continue;
        if (!placed.has(n.anchor)) continue;
        placed.add(n.i);
        offLoop.push(n.i);
        progress = true;
      }
    }
    if (placed.size !== this.nodes.length) {
      throw new Error(`cache mission "${cycle}": ${this.nodes.length - placed.size} node(s) anchored in a cycle`);
    }
    return {
      cycle, tier: this.tier,
      nodes: this.nodes, edges: this.edges,
      entrance, boss,
      loop: this.ring, offLoop,
    };
  }
}

/**
 * NESTING (tier ≥ 1). Grow a bypass off one arc `transit` node: the node keeps its
 * place on the ring and gains two off-loop nodes that rejoin it, so the arc sprouts a
 * sub-loop with something worth the detour in it. ONE level only — Dormans is explicit
 * that coherence degrades with size, and deeper nesting is exactly how that shows up.
 *
 * `hosts` is consumed: each call takes a distinct transit node, so repeated calls never
 * stack bypasses on the same room.
 */
export function nestBypass(b: MissionBuilder, hosts: number[]): boolean {
  const eligible = hosts.filter((i) => b.nodes[i].role === "transit");
  if (eligible.length === 0) return false;
  const host = pick(b.rng, eligible);
  hosts.splice(hosts.indexOf(host), 1);
  const a = b.node("transit", { anchor: host, pressure: 1 });
  const c = b.node(rangeInt(b.rng, 0, 1) === 0 ? "treasure" : "arena", { anchor: a, pressure: 2 });
  b.link(host, a, "tunnel");
  b.link(a, c, "door");
  b.link(c, host, "tunnel");
  return true;
}

/** Apply up to `n` distinct bypasses. Returns how many landed. */
export function nestSome(b: MissionBuilder, hosts: readonly number[], n: number): number {
  const pool = [...hosts];
  let made = 0;
  for (let k = 0; k < n; k++) if (nestBypass(b, pool)) made++;
  return made;
}
